import { useState } from 'react'
import Styles from '../CalendarEventCard.module.scss'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { DESIGN_PATH, DOC_PATH } from 'paths'
import { generatePath, Link } from 'react-router-dom'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import cx from 'classnames'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { ArrowRefresh, CalendarEdit, CopyRight2, Edit3, EyeOpen, MoreDots, Trash2 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { PostPreviewModal } from 'components/PostPreviewModal/PostPreviewModal'
import { useModals } from 'almanac-ui/Modal'
import { PostableChannelType } from 'components/PostPreviewModal/Types'
import { useHover } from '@react-aria/interactions'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import DeleteModal from 'components/NavRightClickMenu/components/DeleteModal/DeleteModal'
import { removeItemFromCalendarCache } from 'hooks/queries/calendar/utils'
import { lowerCase } from 'lodash'
import { FileType } from 'types'
import { useQueryClient } from 'react-query'
import { DuplicateModal } from '../DuplicateModal/DuplicateModal'
import { RescheduleDropdown } from '../RescheduleDropdown/RescheduleDropdown'
import { useFile } from 'hooks/queries/files/useFile'

interface HoverOverlayProps {
  event: AlmanacFileCalendarEvent
  isHovered: boolean
  isSelected?: boolean
  onSelect?: (id: string) => void
  isSelectView?: boolean
  onClick: () => void
  canSelect?: boolean
  /**
   * Home "Upcoming posts" variant: instead of the corner action buttons, show a single
   * centered "Preview" pill that opens the full-screen preview (via `onClick`). Defaults to
   * `false` so the calendar week view keeps its existing hover behavior.
   */
  showCenteredPreviewButton?: boolean
}

export const HoverOverlay = ({
  event,
  isHovered,
  isSelected,
  onSelect,
  isSelectView = false,
  onClick,
  canSelect = true,
  showCenteredPreviewButton = false,
}: HoverOverlayProps) => {
  const { scheduled_post_id, status, channel, asset_type, post_url, id, is_generating, image_url_generating } = event

  const { openModal } = useModals()
  const { isChannelSocialPostable } = useIsChannelPostable()
  const { hoverProps, isHovered: isHoveredOverlay } = useHover({})
  const isPostable = channel && isChannelSocialPostable(channel)
  const postIsCreating = !!event.scheduleContentId && !event.key
  const workspace = useCurrentWorkspace()
  const isFullScreenPreviewEnabled = useFlipperFlag('calendar_full_screen_preview')
  const isDfyCopyDuplicate = useFlipperFlag('dfy_copy_duplicate')
  const queryClient = useQueryClient()
  const { data: fileData } = useFile(event.key)
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false)

  let to = event.id ? generatePath(DESIGN_PATH, { key: event.id }) : ''
  if (asset_type === 'Article') to = event.id ? generatePath(DOC_PATH, { slug: event.id }) : ''

  const openPostPreview = () => {
    if (!scheduled_post_id) return

    openModal(PostPreviewModal, {
      fileId: id,
      scheduledPostId: scheduled_post_id,
      channel: channel as PostableChannelType,
      showSuccessToast: false,
      pollPreviewContentStream: false,
      target: asset_type === 'Design' ? 'design' : 'text',
    })
  }

  const handleClick = () => {
    if (postIsCreating) return

    if (workspace?.is_virtual_marketer && isSelectView) {
      onSelect?.(event.id)
    } else if (isFullScreenPreviewEnabled) {
      onClick()
    }
  }

  const handleDelete = () => {
    if (!event.asset_type) return

    openModal(DeleteModal, {
      files: [{ id: event.key, type: lowerCase(event.asset_type) as FileType, title: event.title }],
      onRequest: () => {
        removeItemFromCalendarCache(queryClient, event.key)
      },
    })
  }

  const handleDuplicate = () => {
    openModal(DuplicateModal, {
      event,
      campaignId: fileData?.campaign_info?.id,
    })
  }

  const handleReschedule = () => setIsRescheduleOpen(true)

  const useLink = !isFullScreenPreviewEnabled && !isSelectView

  if (showCenteredPreviewButton) {
    return (
      <div
        className={cx(Styles.hoverContainer, Styles.centeredPreviewOverlay, {
          [Styles.isHovered]: isHovered || isHoveredOverlay,
        })}
        {...hoverProps}
      >
        <Button
          variant="secondary"
          size="sm"
          rounded
          frontIcon={() => <EyeOpen size={16} />}
          aria-label="Preview"
          onPress={onClick}
          className={Styles.centeredPreviewButton}
        >
          Preview
        </Button>
      </div>
    )
  }

  return (
    <div
      className={cx(Styles.hoverContainer, {
        [Styles.isHovered]: isHovered || isHoveredOverlay,
        [Styles.isSelected]: isSelected,
        [Styles.isSelectView]: isSelectView,
        [Styles.isLoading]: is_generating || image_url_generating,
        [Styles.postIsCreating]: postIsCreating,
      })}
      {...hoverProps}
    >
      {workspace?.is_virtual_marketer && !postIsCreating && canSelect && (
        <div className={Styles.checkboxContainer} onClick={() => onSelect?.(event.id)}>
          <Checkbox size={28} aria-label="Selected image" color="var(--selected-fill)" isSelected={isSelected} />
        </div>
      )}

      {workspace?.is_virtual_marketer && !postIsCreating && canSelect && isDfyCopyDuplicate && (
        <div className={Styles.threeDotMenuContainer}>
          <DropdownTrigger placement="bottom-end">
            <IconButton icon={MoreDots} size="xs" variant="secondary" />
            {(close) => (
              <Menu
                onAction={(key) => {
                  if (key === 'delete') {
                    handleDelete()
                  }
                  if (key === 'duplicate') {
                    handleDuplicate()
                  }
                  if (key === 'reschedule') {
                    handleReschedule()
                  }
                  close()
                }}
                onClose={close}
              >
                <Item key="duplicate" textValue="Duplicate">
                  <CopyRight2 size={16} /> Duplicate
                </Item>
                <Item key="reschedule" textValue="Reschedule">
                  <CalendarEdit size={16} /> Reschedule
                </Item>
                <Item key="delete" textValue="Delete">
                  <Trash2 size={16} /> Delete
                </Item>
              </Menu>
            )}
          </DropdownTrigger>
          <RescheduleDropdown
            event={event}
            isOpen={isRescheduleOpen}
            onOpenChange={setIsRescheduleOpen}
            unscheduleLabel="Unschedule"
          />
        </div>
      )}

      {useLink && <Link to={to} className={Styles.hoverLink} />}
      <div className={cx(Styles.hoverContainerWrapper, { [Styles.useLink]: useLink })} onClick={handleClick}>
        {!isFullScreenPreviewEnabled && (
          <div className={Styles.hoverActionsContainer}>
            {status === PostStatusPropertyValue.Posted && post_url && (
              <div className={Styles.hoverAction}>
                <ButtonLink
                  variant="secondary"
                  size="xs"
                  frontIcon={() => <EyeOpen size={16} />}
                  to={post_url}
                  className={Styles.editButton}
                >
                  See Post
                </ButtonLink>
              </div>
            )}
            {status === PostStatusPropertyValue.Failed && scheduled_post_id && isPostable && (
              <div className={Styles.hoverAction}>
                <Button
                  variant="secondary"
                  size="xs"
                  frontIcon={() => <ArrowRefresh size={16} />}
                  fullWidth
                  onPress={openPostPreview}
                  className={Styles.editButton}
                >
                  Retry
                </Button>
              </div>
            )}
            {status !== PostStatusPropertyValue.Failed && status !== PostStatusPropertyValue.Posted && (
              <div className={Styles.hoverAction}>
                <Button
                  variant="secondary"
                  size="xs"
                  frontIcon={() => <EyeOpen size={16} />}
                  fullWidth
                  aria-label="Preview"
                  onPress={openPostPreview}
                  isDisabled={!scheduled_post_id || !isPostable}
                  className={Styles.editButton}
                >
                  Preview
                </Button>
              </div>
            )}
            <div className={Styles.hoverAction}>
              <ButtonLink
                size="xs"
                fullWidth
                frontIcon={() => <Edit3 size={16} />}
                to={to}
                className={Styles.editButton}
              >
                Edit
              </ButtonLink>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
